<!-- src/apps/workspace/dashboard/DashboardRecent.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import TableSkeleton from '@/shared/components/closet/TableSkeleton.vue';
  import EmptyState from '@/shared/components/ui/EmptyState.vue';
  import ErrorDisplay from '@/shared/components/ui/ErrorDisplay.vue';
  import SecretReceiptTable from '@/apps/secret/components/SecretReceiptTable.vue';
  import { InlineToast } from '@/shared/components/ui/notifications';
  import { useReceiptList } from '@/shared/composables/useReceiptList';
  import { onMounted, computed, ref, onBeforeUnmount } from 'vue';

  // Define props
  interface Props {}
  defineProps<Props>();

  const { t } = useI18n(); // auto-import
  const { details, recordCount, isLoading, refreshRecords, error } = useReceiptList();

  const sectionId = ref(`dashboard-recent-${Math.random().toString(36).substring(2, 9)}`);
  const refreshInterval = ref<number | null>(null);

  // Toast notification state
  const showToast = ref(false);
  const toastMessage = ref('');

  // Add computed properties for revealed and pending receipts
  const revealedReceipts = computed(() => details.value?.revealed_receipts ?? []);
  const pendingReceipts = computed(() => details.value?.pending_receipts ?? []);

  // Set up auto-refresh interval.
  //
  // The manual refresh button that used to sit next to the item count was
  // already dead code (`v-if="false"`); the mock has no such control either,
  // so the button and its handler are gone and the 5-minute poll below is now
  // the only refresh path.
  onMounted(() => {
    refreshRecords();
    refreshInterval.value = window.setInterval(() => {
      refreshRecords();
    }, 300000); // Every 5 minutes
  });

  // Clean up
  onBeforeUnmount(() => {
    if (refreshInterval.value) {
      clearInterval(refreshInterval.value);
    }
  });
</script>

<template>
  <!--
    Recentes (Figma Make, ago/2026): a 760px column, the section title in the
    brand serif with the item count opposite it, then the sent/received card
    stacks. `font-sans` is declared here for the same reason as the account
    surface — the base layer's `html { font-family: ui-serif … }` would
    otherwise render every non-mono label in Georgia.
  -->
  <div class="mx-auto min-w-[320px] max-w-[760px]">
    <section
      :id="sectionId"
      aria-labelledby="dashboard-recent-heading"
      class="mt-8">
      <ErrorDisplay
        v-if="error"
        :error="error" />

      <div v-else-if="isLoading">
        <TableSkeleton />
      </div>

      <div v-else>
        <!-- Section header with count -->
        <div class="mb-5 flex flex-wrap items-baseline justify-between gap-3">
          <h1
            id="dashboard-recent-heading"
            class="mb-0 font-brand text-2xl font-normal text-day-ink dark:text-night-ink">
            {{ t('web.LABELS.title_recent_secrets') }}
          </h1>
          <span
            v-if="recordCount > 0"
            class="text-sm text-day-ink-faint dark:text-night-ink-faint">
            {{ t('web.LABELS.items_count', { count: recordCount }) }}
          </span>
        </div>

        <!-- Content area -->
        <div
          role="region"
          aria-live="polite">
          <SecretReceiptTable
            v-if="recordCount > 0"
            :pending-receipts="pendingReceipts"
            :revealed-receipts="revealedReceipts"
            :is-loading="isLoading"
            :aria-labelledby="'dashboard-recent-heading'" />
          <EmptyState
            :showAction="true"
            v-else
            action-route="/"
            :action-text="t('web.secrets.create_a_secret')">
            <template #title>
              {{ t('web.dashboard.title_no_recent_secrets') }}
            </template>
            <template #description>
              <div>{{ t('web.dashboard.get_started_by_creating_your_first_secret') }}</div>
              <div>{{ t('web.secrets.theyll_appear_here_once_youve_shared_them') }}</div>
            </template>
          </EmptyState>
        </div>
      </div>

      <!-- Toast notification for actions -->
      <InlineToast
        :show="showToast"
        :message="toastMessage"
        aria-live="polite" />
    </section>
  </div>
</template>
